import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { Toaster } from '@/components/ui/toaster';
import { AuthProvider } from '@/hooks/use-auth';
import { SocketProvider } from '@/hooks/use-socket';
import { ProtectedRoute } from '@/components/auth/protected-route';
import { AdminRoute } from '@/components/auth/admin-route';
import { ManagementRoute } from '@/components/auth/management-route';
import { InternalRoute } from '@/components/auth/internal-route';
import { AppLayout } from '@/components/layout/app-layout';
import { PlanningLayout } from '@/components/layout/planning-layout';
import { WorkGroupsLayout } from '@/components/layout/workgroups-layout';
import { AuthLayout } from '@/components/layout/auth-layout';
import { LoginPage } from '@/pages/login';
import { RegisterPage } from '@/pages/register';
import { ForgotPasswordPage } from '@/pages/forgot-password';
import { ResetPasswordPage } from '@/pages/reset-password';
import { InvitePage } from '@/pages/invite';
import { DashboardPage } from '@/pages/dashboard';
import { ProjectsPage } from '@/pages/projects';
import { ProjectDashboardPage } from '@/pages/project-dashboard';
import { ProjectBoardPage } from '@/pages/project-board';
import { TaskDetailPage } from '@/pages/task-detail';
import { NotificationsPage } from '@/pages/notifications';
import { SettingsPage } from '@/pages/settings';
import { SearchPage } from '@/pages/search';
import { UsersPage } from '@/pages/users';
import { TeamsPage } from '@/pages/teams';
import { PermissionsPage } from '@/pages/permissions';
import { CompanyChatsPage } from '@/pages/company-chats';
import { TagManagerPage } from '@/pages/tag-manager';
import { StartPage } from '@/pages/start';
import { PlanningPage } from '@/pages/planning';
import { PlanningWeekPage } from '@/pages/planning-week';
import { PlanningProductionPage } from '@/pages/planning-production';
import { PlanningCatalogPage } from '@/pages/planning-catalog';
import { SecurityPage } from '@/pages/security';
import { WorkGroupsPage } from '@/pages/workgroups';
import { WorkGroupsDetailPage } from '@/pages/workgroups-detail';
import { WorkGroupsFolderPage } from '@/pages/workgroups-folder';
import { WorkGroupsTrashPage } from '@/pages/workgroups-trash';

export default function App() {
  return (
    <AuthProvider>
      <SocketProvider>
        <Routes>
          {/* Auth routes */}
          <Route element={<AuthLayout />}>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
          </Route>

          {/* Public invite route (not in auth layout) */}
          <Route path="/invite/:token" element={<InvitePage />} />

          {/* Module selection route (standalone, no layout) */}
          <Route
            path="/start"
            element={
              <ProtectedRoute>
                <StartPage />
              </ProtectedRoute>
            }
          />

          {/* WePlanning routes - use path prefix on layout to avoid pathless route issues */}
          <Route
            path="/planning"
            element={
              <ProtectedRoute>
                <PlanningLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<PlanningPage />} />
            <Route path="weeks/:weekId" element={<PlanningWeekPage />} />
            <Route path="production" element={<PlanningProductionPage />} />
            <Route path="catalog" element={<PlanningCatalogPage />} />
          </Route>

          <Route
            path="/workgroups"
            element={
              <ProtectedRoute>
                <InternalRoute anyOfCapabilities={['workgroups.view']}>
                  <WorkGroupsLayout />
                </InternalRoute>
              </ProtectedRoute>
            }
          >
            <Route index element={<WorkGroupsPage />} />
            <Route path=":workGroupId/trash" element={<WorkGroupsTrashPage />} />
            <Route path=":workGroupId/folders/:folderId" element={<WorkGroupsFolderPage />} />
            <Route path=":workGroupId" element={<WorkGroupsDetailPage />} />
          </Route>

          {/* Root redirect */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />

          {/* WeTicket shell — single persistent AppLayout across all routes below */}
          <Route
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/projects" element={<Outlet />}>
              <Route index element={<ProjectsPage />} />
              <Route path=":projectId" element={<ProjectDashboardPage />} />
              <Route path=":projectId/lists/:listId" element={<ProjectBoardPage />} />
            </Route>
            <Route path="/tasks/:taskId" element={<TaskDetailPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route
              path="/users"
              element={
                <AdminRoute>
                  <UsersPage />
                </AdminRoute>
              }
            />
            <Route
              path="/teams"
              element={
                <ManagementRoute>
                  <TeamsPage />
                </ManagementRoute>
              }
            />
            <Route
              path="/permissions"
              element={
                <InternalRoute anyOfCapabilities={['permissions.manage']}>
                  <PermissionsPage />
                </InternalRoute>
              }
            />
            <Route
              path="/company-chats"
              element={
                <InternalRoute anyOfCapabilities={['chat.company.view', 'chat.project.view']}>
                  <CompanyChatsPage />
                </InternalRoute>
              }
            />
            <Route path="/tag-manager" element={<TagManagerPage />} />
            <Route
              path="/security"
              element={
                <InternalRoute anyOfCapabilities={['security.view']}>
                  <SecurityPage />
                </InternalRoute>
              }
            />
          </Route>

          {/* Catch-all redirect */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        <Toaster />
      </SocketProvider>
    </AuthProvider>
  );
}
